/**
 * RequireAuth decision: render the app, show the session splash, go to /login, or say the
 * backend is UNREACHABLE.
 *
 * WHY (2026-10-31, follow-up to !315/!317): RequireAuth bounced to /login on ANY failure of
 * GET /api/v2/me that was not a 401 -- including a backend that is simply down. The operator
 * landed on a sign-in card, signing in again could not work, and nothing said the controller
 * was unreachable. A transport error (lib/api/client ApiError status 0: DNS, refused,
 * offline) and the gateway's 502/503/504 (the BFF answering for a controller that is down)
 * now show "unreachable, retry". A 401 still goes to /login, and any other failure keeps the
 * old /login fallback so a deep link can never render empty chrome.
 *
 * Pure + dependency-free (duck-typed `status`, no import of the api client) so
 * `npm run test:unit` exercises it with Node's runner.
 */

export type AuthGateState = "loading" | "render" | "login" | "unreachable";

/** Statuses that mean "the backend could not be reached", not "you are not signed in". */
const UNREACHABLE_STATUSES = new Set([0, 502, 503, 504]);

function statusOf(error: unknown): number | undefined {
  if (error && typeof error === "object" && "status" in error) {
    const s = (error as { status: unknown }).status;
    return typeof s === "number" ? s : undefined;
  }
  return undefined;
}

export function authGateState(q: {
  isLoading: boolean;
  data: unknown;
  error: unknown;
}): AuthGateState {
  if (q.isLoading) return "loading";
  // Identity already hydrated wins over a later refetch error: no flicker to the error card.
  if (q.data) return "render";
  const status = statusOf(q.error);
  if (status === 401) return "login";
  if (status !== undefined && UNREACHABLE_STATUSES.has(status)) return "unreachable";
  return "login";
}
